<template>
    <div style="height: 100%; width: 100%" class="map-container">
        <l-map
            v-if="showMap"
            :zoom="zoom"
            :center="center"
            :options="mapOptions"
            style="height: 100vh"
            @update:center="centerUpdate"
            @update:zoom="zoomUpdate"
            :bounds="bounds"
            @click="clearModal"
            ref="map"
        >
            <extraordinary-condition-markers v-if="showExtraordinary" />
            <l-tile-layer :url="url" :attribution="attribution" />
            <marker-wrapper v-if="markersCount > 0"></marker-wrapper>
            <highway-marker-wrapper
                v-if="highwayMarkersCount > 0"
            ></highway-marker-wrapper>
            <div v-if="routes.length">
                <l-routing-machine
                    v-for="(route, index) in routes"
                    :key="`${index}-${route[0].lat}-${route[1].lng}-${
                        route[2] ? route[2].lng : 0
                    }- ${route[3] ? route[3].lng : 0}- ${routes.length}`"
                    :waypoints="route"
                    :fitSelectedRoutes="fitSelected(route)"
                />
            </div>
            <div v-if="staticRoutes.length">
                <l-highway-routes
                    v-for="(route, index) in staticRoutes"
                    :key="`${index}-${route[0].lat}-${route[1].lng}`"
                    :waypoints="route"
                />
            </div>
            <div v-if="routes.length && routePoints.length">
                <route-marker
                    v-for="(marker, index) in routePoints"
                    :key="`${marker.id}-${marker.type}-${index}`"
                    :coordinates="marker"
                >
                </route-marker>
            </div>
        </l-map>

        <conditions-filter />
        <conditions-modal v-if="showExtraordinary" />
    </div>
</template>

<script>
import { latLng, Icon, latLngBounds } from "leaflet";
import { LMap, LTileLayer, LMarker, LPopup, LTooltip } from "vue2-leaflet";
import { mapGetters } from "vuex";
import LRoutingMachine from "./LRoutingMachine";
import LHighwayRoutes from "./LHighwayRoutes";
import MarkerWrapper from "./MarkerWrapper";
import RouteMarker from "./RouteMarker";
import ExtraordinaryConditionMarkers from "./ExtraordinaryConditionMarkers";
import ConditionsFilter from "./components/ConditionsFilter";
import HighwayMarkerWrapper from "./HighwayMarkerWrapper";
import ConditionsModal from "./modals/ConditionsModal";
import Vue from "vue";

delete L.Icon.Default.prototype._getIconUrl;
Icon.Default.mergeOptions({
    iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
    iconUrl: require("leaflet/dist/images/marker-icon.png"),
    shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

export default {
    name: "Leaflet",
    components: {
        LMap,
        LTileLayer,
        LMarker,
        LPopup,
        LTooltip,
        LRoutingMachine,
        LHighwayRoutes,
        MarkerWrapper,
        HighwayMarkerWrapper,
        RouteMarker,
        ExtraordinaryConditionMarkers,
        ConditionsFilter,
        ConditionsModal,
    },
    props: {
        initialBounds: {
            default: () =>
                latLngBounds([
                    [46.55, 16.366667],
                    [42.4, 18.533333],
                    [45.2, 19.283333],
                    [45.483333, 13.5],
                ]),
        },
        initialCenter: {
            default: () => latLng(44.51192318118304, 16.391666500000017),
        },
    },
    data() {
        return {
            zoom: 8,
            center: this.initialCenter,
            url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
            attribution:
                '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors',
            bounds: this.initialBounds,
            staticRoutes: [],
            showParagraph: false,
            mapOptions: {
                zoomSnap: 0.5,
            },
            showMap: true,
            routes: [],
            routePoints: [],
            last: null,
        };
    },
    methods: {
        zoomUpdate(zoom) {
            this.zoom = zoom;
            this.$store.commit("zoomUpdate", zoom);
        },

        centerUpdate(center) {
            this.center = center;
        },

        clearModal() {
            this.$store.commit("setModal", null);
        },

        fitSelected(route) {
            if (this.last) {
                let highwayData = this.highways.find((highway) => {
                    return (
                        this.last === highway.id &&
                        latLng(highway.start_coordinates).lat === route[0].lat
                    );
                });
                return !!(highwayData && highwayData.id);
            } else
                return !!(
                    this.filters &&
                    this.filters.highways &&
                    this.filters.highways.length === 1
                );
        },
    },

    computed: {
        ...mapGetters([
            "filters",
            "markersCount",
            "highwayMarkersCount",
            "highways",
            "tollCoordinates",
            "markers",
            "tollPoints",
            "fitBoundsFlag",
            "extraordinary",
            "showExtraordinary",
        ]),
    },

    watch: {
        filters(newValue, oldValue) {
            let vm = this;
            let newRoutes;
            if (
                newValue &&
                newValue["highways"] &&
                newValue["highways"].length
            ) {
                let highways = newValue["highways"];
                newRoutes = highways.map((selected) => {
                    let highway = this.highways.find(
                        (el) => el.id === selected
                    );
                    return [
                        latLng(highway.start_coordinates),
                        latLng(highway.end_coordinates),
                    ];
                });
                // zoom on last added highway
                let newHighways = newValue.highways;
                let oldHighways = oldValue.highways;
                if (
                    oldHighways &&
                    oldHighways.length &&
                    newHighways.length > oldHighways.length
                ) {
                    newHighways.forEach((highway) => {
                        if (!oldHighways.find((old) => old === highway)) {
                            this.last = highway;
                        }
                    });
                } else {
                    this.last = null;
                    this.$refs.map.setCenter(this.initialCenter);
                    this.zoom = 7.5;
                }
            } else {
                newRoutes = [];
                this.$refs.map.setCenter(this.initialCenter);
                this.zoom = 7.5;
            }
            Vue.set(this, "routes", newRoutes);
        },

        tollCoordinates(newValue) {
            let newRoutes;

            if (newValue && newValue.length) {
                newRoutes = newValue.map((route) => {
                    if (route && route.length) {
                        return route.map((coords) => {
                            if (coords && coords.length) {
                                return latLng(coords);
                            }
                        });
                    }
                });
            } else {
                newRoutes = [];
            }

            Vue.set(this, "routes", newRoutes);
        },

        highways(newValue) {
            let routes = [];

            if (newValue && newValue.length) {
                routes = newValue.map((route) => {
                    return [
                        latLng(route.start_coordinates),
                        latLng(route.end_coordinates),
                    ];
                });
            }

            Vue.set(this, "staticRoutes", routes);
        },

        markers(newVal) {
            if (
                Object.keys(newVal) &&
                newVal.searchResult &&
                newVal.searchResult.length
            ) {
                let newBound = newVal.searchResult[0].coordinates;
                this.center = latLng(newBound);
            } else {
                this.bounds = this.initialBounds;
            }
        },

        tollPoints(newValue) {
            let points = [];

            if (newValue && newValue.length) {
                points = newValue;
            }

            Vue.set(this, "routePoints", points);
        },

        fitBoundsFlag(val) {
            if (val) {
                this.$refs.map.setCenter(this.initialCenter);
                this.zoom = 7.5;
                this.$store.commit("stopFitBounds");
            }
        },
    },
};
</script>
